import React, { useState, useEffect, useCallback } from "react";
import { Aurora, Card, NowBookingPill, PatentPendingBadge } from "./Shell";
import Instructions from "./Instructions";
import EducationalSections from "./EducationalSections";
import PeakStateTeaser from "./PeakStateTeaser";
import ProgressReminderBanner from "./ProgressReminderBanner";
import RecoveryScoreCard from "./RecoveryScoreCard";
import SiteFooter from "./SiteFooter";
import { markProgressCheckIn } from "../lib/memberIdentity";
import ConciergeChat from "./ConciergeChat";
import useInstructionsVisibility from "../hooks/useInstructionsVisibility";
import { wakeMaya, wakeElara } from "../lib/maya";
import {
  getAdaptivePlan,
  getAdaptiveCtaLabel,
  getHighlightedCodes,
  getBandTint,
} from "../lib/adaptiveRouting";
import { useScrollReveal } from "../hooks/useScrollReveal";

const BRAIN_IMG = "/hero-nervous-system.webp";

const IW_SHIELD =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/amgkh1mp_logo.png";

const ACTIVATION_FORM_URL = "https://integratedwellness.jotform.com/262077736119058";
const PROGRESS_FORM_URL = "https://integratedwellness.jotform.com/262077987278072";

const NAME_STORAGE_KEY = "iwmc.bls.patient_name";

const PROGRAMS_URL = "https://integrated-wellness-6.emergent.host/";
const MEDICAL_URL = "https://integratednycai.com/medical-care";

/**
 * Detect the platform-appropriate paste shortcut so instructions read
 * naturally on every device. Falls back to the universal "Ctrl + V".
 */
function detectPasteShortcut() {
  if (typeof navigator === "undefined") return "Ctrl + V";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toLowerCase();
  const isApple =
    /mac|iphone|ipad|ipod/.test(platform) || /mac|iphone|ipad/.test(ua);
  return isApple ? "\u2318 V" : "Ctrl + V";
}

// Static "what to expect" steps — module-level so we don't allocate a
// new array on every Welcome render.
const SESSION_FLOW_STEPS = [
  { strong: "1. Safety check-in", text: "six brief questions. Certain answers (active self-harm, crisis) route you to immediate human support." },
  { strong: "2. Pre-session check-in", text: "share how you've been feeling (PHQ-4, GAD-7) and where you're at right now (SUDS 0–10)." },
  { strong: "3. Choose your practice", text: "today's available practice is bilateral rhythm — Mode A (tap-based) or Mode B (hands-free). You can layer breathing, humming, butterfly hug, or audio." },
  { strong: "4. Guided session", text: "2–5 minutes typically. Pacer auto-tunes to where you're at. A red STOP button is always visible." },
  { strong: "5. Post-session check-in", text: "re-rate the same scales to see how you've shifted." },
  { strong: "6. Grounding exit (5-4-3-2-1)", text: "a brief sensory ritual closes the session gently." },
  { strong: "7. Session summary", text: "shifts, telemetry, wearable timestamps. Sessions auto-save to your wellness record." },
];

// The full Nervous System Reset toolkit. Six modalities, all live in the program.
const TOOLKIT = [
  {
    code: "TK.01",
    flag: "Live",
    flagTone: "live",
    title: "Breathwork",
    sub: "Slow paced breathing + physiological sigh.",
    copy:
      "Simple paced breathing and the 'sigh' technique to help your body settle quickly. Layered into every session.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/d84c68wj_eNZeOEv0c9rK0BFt.png",
    imageAlt: "The full nervous system illuminated as the breath activates the calming branch",
    prompt: "Walk me through a breathwork practice.",
  },
  {
    code: "TK.02",
    flag: "Live",
    flagTone: "live",
    title: "Grounding",
    sub: "5-4-3-2-1 and gentle sensory anchoring.",
    copy:
      "Easy sensory and muscle-relaxation techniques that bring you back to the present. Closes every session.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/e2p2gg5s_A4ujK1kVMiRqD4PR.png",
    imageAlt: "A member seated in stillness — grounded, settled, present",
    prompt: "Walk me through a grounding practice.",
  },
  {
    code: "TK.03",
    flag: "Featured · BLS",
    flagTone: "featured",
    title: "Bilateral Rhythm",
    sub: "Soft, alternating sound or taps.",
    copy:
      "Two ways to practice: tap-based for active engagement, hands-free for body-focused practice. Many members find this calming and focusing.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/3eav18qp_jRFFOsoFOzWuh8Rw.png",
    imageAlt: "Bilateral sound rhythm — left-right hemispheric resonance",
    prompt: "Walk me through a bilateral rhythm practice.",
  },
  {
    code: "TK.04",
    flag: "Live",
    flagTone: "live",
    title: "Visualization",
    sub: "Guided imagery for safe-place + calm-anchoring.",
    copy:
      "Short, agent-led visualizations to help you visit a calmer internal state — and bring some of it back with you.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/i1gz5du3_f3B2AHyq67vZzCYA.png",
    imageAlt: "A safe place visualized inside the mind — landscape held in the brain",
    prompt: "Walk me through a visualization practice.",
  },
  {
    code: "TK.05",
    flag: "Live",
    flagTone: "live",
    title: "Muscle Release",
    sub: "Tension hold-and-let-go (PMR).",
    copy:
      "Progressive muscle relaxation — tense, hold, release — to soften where stress sits in the body.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/4523yka4_PIYBHuVCxhmrz_Hs.png",
    imageAlt: "Progressive muscle relaxation — phase: forearms / fists (hold)",
    prompt: "Walk me through a progressive muscle relaxation cycle.",
  },
  {
    code: "TK.06",
    flag: "Live",
    flagTone: "live",
    title: "Journal Reflection",
    sub: "Brief, guided prompts after each session.",
    copy:
      "A short, agent-prompted note to capture what shifted and set one small intention for the rest of your day.",
    image:
      "https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/c5mfe4az_ufNdWfv0zBVIW2jh.png",
    imageAlt: "A member sitting calmly with a journal at the close of a session",
    prompt: "Help me with a journal reflection prompt.",
  },
];

// Five-step "how it works" — mirrors the public brief.
// Each step has an `elaraPrompt` so hovering/clicking the card can wake
// Elara (the program guide) pre-loaded with that step's question.
const STEPS = [
  {
    n: "01",
    title: "Check in.",
    copy: "A quick, private check-in — how wound-up are you right now?",
    elaraPrompt: "How does the check-in work, and what kind of questions will it ask me?",
  },
  {
    n: "02",
    title: "We personalize.",
    copy: "The guide picks practices that fit your state today.",
    elaraPrompt: "How does the program personalize practices based on how I'm feeling?",
  },
  {
    n: "03",
    title: "You're guided.",
    copy: "A calm, step-by-step session leads you through — one breath at a time.",
    elaraPrompt: "What does a guided session look like? Walk me through what to expect.",
  },
  {
    n: "04",
    title: "You reflect.",
    copy: "A short prompt — what shifted? Set one small intention.",
    elaraPrompt: "How does the reflection step work, and why is it important?",
  },
  {
    n: "05",
    title: "You track progress.",
    copy: "Each session updates your personal recovery trend over time.",
    elaraPrompt: "How does the program track my progress, and what data does it use?",
  },
];

// FAQ — straight from the public brief, wellness-coaching tone.
const FAQ = [
  {
    q: "Is this therapy or medical care?",
    a: "No. It's a wellness program that supports relaxation and recovery. It isn't medical or mental-health care and doesn't replace it.",
  },
  {
    q: "Do I need any equipment?",
    a: "No. A wearable is optional and just adds detail to your recovery trend.",
  },
  {
    q: "How long is a session?",
    a: "Usually 5 to 15 minutes.",
  },
  {
    q: "Is my check-in private?",
    a: "Your information is kept in IWMC's secure system so your wellness team can support you. Questions? Contact us below.",
  },
  {
    q: "What if I'm having a hard time right now?",
    a: "If you're in crisis or thinking about harming yourself, please call or text 988, or call 911. This program is not a crisis service.",
  },
  {
    q: "Are all six modalities available?",
    a: "Yes. Breathwork, grounding, bilateral rhythm, visualization, muscle release, and journal reflection are all part of the Nervous System Reset program — each with its own agent-guided experience.",
  },
];

export default function Welcome({ onStart, onDemo }) {
  useScrollReveal();
  const inst = useInstructionsVisibility();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // L2 Adaptive Routing — self-reported activation 0–10 (or null).
  // null means "no reading yet" so we render a neutral hero without
  // implying a recommendation the member didn't ask for.
  const [activation, setActivation] = useState(null);
  const adaptivePlan = getAdaptivePlan(activation);
  const highlightedCodes = getHighlightedCodes(activation);

  useEffect(() => {
    try {
      const savedName = localStorage.getItem(NAME_STORAGE_KEY);
      if (savedName) setName(savedName);
      // Email is intentionally NOT persisted locally (privacy-safe member
      // key architecture) — members re-enter it per visit if they want
      // their session linked to their IW record.
    } catch (err) {
      console.warn("[Welcome] could not read remembered identity:", err);
    }
  }, []);

  const submit = (handler) => () => {
    const cleanName = (name || "").trim();
    const cleanEmail = (email || "").trim().toLowerCase();
    if (cleanName) {
      try {
        localStorage.setItem(NAME_STORAGE_KEY, cleanName);
      } catch (err) {
        console.warn("[Welcome] could not persist name locally:", err);
      }
    }
    handler({
      name: cleanName || null,
      email: cleanEmail || null,
      activation: activation === null ? null : Number(activation),
    });
  };

  // Generic AI-coach launcher — both Maya (technique walkthroughs from
  // the Toolkit) and Elara (program guide questions from the How-It-Works
  // steps) use this single toast UX so the experience feels coherent.
  const [agentToast, setAgentToast] = useState(null);
  const launchAgentWithPrompt = useCallback(async (agentKey, prompt, label) => {
    const waker = agentKey === "elara" ? wakeElara : wakeMaya;
    const { opened, copied } = await waker(prompt);
    setAgentToast({
      agentKey,
      agentName: "Elara",
      label,
      prompt,
      opened,
      copied,
      pasteShortcut: detectPasteShortcut(),
      stamp: Date.now(),
    });
  }, []);
  const launchMayaWithPrompt = useCallback(
    (prompt, label) => launchAgentWithPrompt("maya", prompt, label),
    [launchAgentWithPrompt]
  );
  const launchElaraWithPrompt = useCallback(
    (prompt, label) => launchAgentWithPrompt("elara", prompt, label),
    [launchAgentWithPrompt]
  );

  useEffect(() => {
    if (!agentToast) return undefined;
    // Stay visible long enough for members to read the 3 steps + paste.
    const t = setTimeout(() => setAgentToast(null), 8500);
    return () => clearTimeout(t);
  }, [agentToast]);

  return (
    <Aurora>
      <AgentPromptToast toast={agentToast} onClose={() => setAgentToast(null)} />
      <div id="main" tabIndex={-1} className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-10 sm:pt-16 pb-20" style={{ outline: "none" }}>
        {/* Weekly Progress reminder — returning members only */}
        <ProgressReminderBanner />

        {/* HERO — Nervous System Reset framing */}
        <div className="grid lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-16 items-center">
          <div className="fade-up order-1">
            <div className="flex items-center gap-4 mb-8">
              <img
                src={IW_SHIELD}
                alt="Integrated Wellness"
                width={84}
                height={84}
                className="flex-shrink-0"
                style={{ filter: "drop-shadow(0 8px 20px rgba(17, 41, 107, 0.30))" }}
              />
              <div className="leading-tight">
                <div className="serif text-2xl sm:text-3xl" style={{ color: "var(--iw-primary)" }}>
                  Integrated Wellness
                </div>
                <div
                  className="iw-mono uppercase mt-1.5"
                  style={{
                    color: "var(--iw-accent-red)",
                    fontSize: "11px",
                    letterSpacing: "0.24em",
                    fontWeight: 600,
                  }}
                >
                  Nervous System Reset
                </div>
              </div>
            </div>

            <div className="mb-5 flex flex-wrap items-center gap-2">
              <NowBookingPill label="Open · Booking Members Now" />
              <span
                className="iw-mono uppercase px-2.5 py-1"
                style={{
                  background: "var(--iw-accent-red)",
                  color: "#fff",
                  borderRadius: "2px",
                  fontSize: "10px",
                  letterSpacing: "0.20em",
                  fontWeight: 600,
                }}
                title="Wellness coaching program — not medical care"
                data-testid="wellness-lane-tag"
              >
                Wellness · Non-Medical
              </span>
            </div>

            <h1
              className="serif"
              style={{
                fontSize: "clamp(2.6rem, 6vw, 4.75rem)",
                lineHeight: 0.98,
                color: "var(--iw-primary)",
              }}
            >
              Reset your<br />
              nervous<br />
              <span style={{ color: "var(--iw-accent-red)" }}>system.</span>
            </h1>

            <p
              className="mt-7 max-w-xl text-lg sm:text-xl leading-relaxed"
              style={{ color: "var(--iw-body)" }}
            >
              Short, guided wellness sessions —{" "}
              <strong style={{ color: "var(--iw-primary)" }}>breathwork</strong>,{" "}
              <strong style={{ color: "var(--iw-primary)" }}>grounding</strong>,{" "}
              <strong style={{ color: "var(--iw-primary)" }}>bilateral rhythm</strong>,{" "}
              <strong style={{ color: "var(--iw-primary)" }}>visualization</strong>, and{" "}
              <strong style={{ color: "var(--iw-primary)" }}>reflection</strong> — personalized to
              how you feel that day.
            </p>
            <p
              className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--iw-muted)" }}
            >
              Six modalities, one personalized reset. Every session adapts to where you're at
              today.
            </p>

            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 w-full">
                <div className="flex flex-col w-full sm:w-72">
                  <label
                    htmlFor="welcome-name"
                    className="iw-mono uppercase mb-1.5"
                    style={{
                      color: "var(--iw-muted)",
                      fontSize: "10px",
                      letterSpacing: "0.22em",
                      fontWeight: 600,
                    }}
                  >
                    Your name <span style={{ opacity: 0.7 }}>(for your wellness record)</span>
                  </label>
                  <input
                    id="welcome-name"
                    type="text"
                    autoComplete="name"
                    placeholder="First Last"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="px-4 py-3 bg-white text-base outline-none transition-all"
                    style={{
                      border: "1px solid var(--iw-border)",
                      borderRadius: "2px",
                      color: "var(--iw-primary)",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                    data-testid="welcome-name-input"
                  />
                </div>
                <div className="flex flex-col w-full sm:w-72">
                  <label
                    htmlFor="welcome-email"
                    className="iw-mono uppercase mb-1.5"
                    style={{
                      color: "var(--iw-muted)",
                      fontSize: "10px",
                      letterSpacing: "0.22em",
                      fontWeight: 600,
                    }}
                  >
                    Email <span style={{ opacity: 0.7 }}>(optional)</span>
                  </label>
                  <input
                    id="welcome-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="px-4 py-3 bg-white text-base outline-none transition-all"
                    style={{
                      border: "1px solid var(--iw-border)",
                      borderRadius: "2px",
                      color: "var(--iw-primary)",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                    data-testid="welcome-email-input"
                  />
                </div>
              </div>

              {/* L2 Adaptive Routing — activation dial + suggested path */}
              <ActivationDial
                activation={activation}
                setActivation={setActivation}
                plan={adaptivePlan}
              />

              <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                <button
                  type="button"
                  className="iw-btn-primary w-full sm:w-auto"
                  onClick={submit(onStart)}
                  data-testid="welcome-begin-btn"
                >
                  {getAdaptiveCtaLabel(activation, "Start with a quick check-in")}{" "}
                  <span aria-hidden="true">↗</span>
                </button>
                {onDemo && (
                  <button
                    type="button"
                    className="iw-btn-ghost"
                    onClick={submit(onDemo)}
                    data-testid="welcome-demo-btn"
                    title="Auto-play a complete demonstration of the full session flow"
                  >
                    ▶ Run Demo
                  </button>
                )}
              </div>
            </div>

            <div
              className="mt-5 text-sm leading-relaxed max-w-xl"
              style={{ color: "var(--iw-muted)" }}
            >
              Check-in → personalized practice → grounding exit → reflection. Stoppable at any
              time. If you enter your email, your session links to your IW wellness record so your
              team can support you.{" "}
              <a
                href={ACTIVATION_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
                style={{ color: "var(--iw-accent-red)", fontWeight: 600 }}
                data-testid="hero-activation-link"
              >
                New member? Complete your Activation Assessment ↗
              </a>
            </div>

            {!inst.visible && (
              <button
                type="button"
                onClick={inst.show}
                className="iw-btn-ghost mt-6"
                style={{ padding: "0.6rem 1.1rem", fontSize: "11px" }}
                data-testid="welcome-show-instructions-btn"
              >
                ↺ Show Instructions
              </button>
            )}
          </div>

          {/* Brain hero visual */}
          <div className="order-2 fade-up">
            <div className="relative">
              <div
                className="absolute -inset-4"
                style={{
                  background:
                    "radial-gradient(60% 50% at 50% 50%, rgba(30,58,138,0.20), rgba(217,4,41,0.08) 55%, transparent 70%)",
                  filter: "blur(24px)",
                }}
                aria-hidden="true"
              />
              <div
                className="relative overflow-hidden"
                style={{
                  border: "1px solid var(--iw-border)",
                  borderRadius: "2px",
                  boxShadow: "0 30px 80px -30px rgba(17, 41, 107, 0.40)",
                }}
              >
                <div
                  className="absolute top-3 left-3 z-10 px-2.5 py-1 iw-mono uppercase"
                  style={{
                    background: "rgba(255,255,255,0.95)",
                    border: "1px solid var(--iw-border)",
                    borderRadius: "2px",
                    color: "var(--iw-primary)",
                    fontSize: "9px",
                    letterSpacing: "0.18em",
                    fontWeight: 600,
                  }}
                >
                  Reset · Live
                </div>
                <div
                  className="absolute top-3 right-3 z-10 px-2.5 py-1 iw-mono uppercase inline-flex items-center gap-1.5"
                  style={{
                    background: "rgba(255,255,255,0.95)",
                    border: "1px solid var(--iw-border)",
                    borderRadius: "2px",
                    color: "var(--iw-accent-red)",
                    fontSize: "9px",
                    letterSpacing: "0.18em",
                    fontWeight: 600,
                  }}
                >
                  <span className="iw-now-dot" /> Settling
                </div>
                <img
                  src={BRAIN_IMG}
                  alt="A calmer nervous system — soft rhythm settling the body"
                  className="w-full h-auto block"
                  width="1376"
                  height="768"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                {/* No text overlay — the artwork carries its own program
                    title; overlaying more copy duplicated the branding and
                    hurt legibility at narrow widths. */}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Pill>Breathwork</Pill>
                <Pill>Grounding</Pill>
                <Pill>Bilateral</Pill>
                <Pill>Visualization</Pill>
                <Pill>Muscle Release</Pill>
                <Pill>Journal</Pill>
              </div>
            </div>
          </div>
        </div>

        {/* § 01 — YOUR TOOLKIT (6 modality cards) */}
        <section id="toolkit" className="mt-24 sm:mt-32 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 01 — Your Toolkit</div>
            <h2 className="iw-section-head__title">
              Six modalities. One personalized reset.
            </h2>
            <p
              className="text-base sm:text-lg max-w-3xl"
              style={{ color: "var(--iw-body)" }}
            >
              Every session pulls from the practices that fit you best today. Breathwork and
              grounding are layered into every session — bilateral rhythm, visualization, muscle
              release, and journal reflection each have their own agent-guided experience.
            </p>
          </div>
          {/* Persistent how-to-use callout — taught once so members know
              what a click does before they try one. */}
          <AskMayaHowTo className="mb-7" />
          {adaptivePlan && (
            <ToolkitAdaptiveBanner
              plan={adaptivePlan}
              activation={activation}
            />
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOOLKIT.map((t) => (
              <ToolkitCard
                key={t.code}
                {...t}
                onLaunch={launchMayaWithPrompt}
                highlighted={highlightedCodes.has(t.code)}
                highlightTone={adaptivePlan ? adaptivePlan.tone : null}
                highlightRank={
                  adaptivePlan
                    ? adaptivePlan.chain.findIndex((s) => s.code === t.code)
                    : -1
                }
              />
            ))}
          </div>
        </section>

        {/* § 02 — HOW IT WORKS */}
        <section id="how-it-works" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 02 — How it works</div>
            <h2 className="iw-section-head__title">
              Five steps. About 5–15 minutes.
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {STEPS.map((s) => (
              <StepCard key={s.n} step={s} onLaunch={launchElaraWithPrompt} />
            ))}
          </div>
        </section>

        {/* § 03 — BLS · TWO WAYS TO PRACTICE */}
        <section id="rhythm" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 03 — Bilateral · Available Now</div>
            <h2 className="iw-section-head__title">
              The BLS console. Two ways to practice.
            </h2>
            <p className="text-base sm:text-lg max-w-2xl" style={{ color: "var(--iw-body)" }}>
              Bilateral rhythm is the first modality available — patent-pending. Start with one and
              switch as your nervous system calls for it.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <ModeCard
              code="MO.A"
              flag="Active"
              title="Tap-Based"
              sub="Self-paced rhythm. Hands stay busy."
              copy="Tap left and right targets at your own cadence. Good for restless or wound-up moments. Pair with humming, tongue-to-roof, or extended exhalation."
              image="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/dxxh75w0_JZmk3KouEELMd5wu.png"
              imageAlt="Hands tapping a soft surface at a steady left-right cadence"
            />
            <ModeCard
              code="MO.B"
              flag="Receptive"
              title="Hands-Free"
              sub="A smooth dot traverses the screen at your cadence."
              copy="Eyes track, hands stay free. Pair with the butterfly hug, breathing pacer, or self-soothing touch. Optional webcam gaze tracking when permitted."
              image="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/qprbon9g_6h5qiUhKDY6wCpGV.png"
              imageAlt="Eyes closed, body settled — receptive bilateral practice"
            />
          </div>
        </section>

        {/* Domains / Educational sections */}
        <EducationalSections />

        {/* § 06 — Check-ins (JotForm → Notion writeback happens form-side) */}
        <section id="checkins" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 06 — Check-ins</div>
            <h2 className="iw-section-head__title">
              Two forms. Your whole journey, tracked.
            </h2>
            <p className="text-base sm:text-lg max-w-2xl" style={{ color: "var(--iw-body)" }}>
              Your answers sync straight to your Integrated Wellness record, so your wellness
              team can follow your progress and tune your coaching.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <CheckinCard
              step="01 · Once, at the start"
              title="Activation Assessment"
              copy="Your baseline: goals, current state, sleep, lifestyle. About 5 minutes — save and resume anytime."
              cta="Complete your activation"
              href={ACTIVATION_FORM_URL}
              accent="var(--iw-accent-red)"
              testId="checkin-activation-card"
            />
            <CheckinCard
              step="02 · Weekly"
              title="Progress Check-in"
              copy="Ten quick ratings plus your week's practice. About 3 minutes — keeps your coaching on track."
              cta="Log this week"
              href={PROGRESS_FORM_URL}
              accent="var(--iw-blue)"
              testId="checkin-progress-card"
              onCtaClick={markProgressCheckIn}
            />
          </div>
          {/* Self-Reported Recovery Score — check anytime, collapsed by default */}
          <RecoveryScoreCard collapsible className="mt-5" />
        </section>

        {/* § 07 — What to expect */}
        <section id="grounding-practice" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 07 — Inside a Session</div>
            <h2 className="iw-section-head__title">
              Seven gentle steps from start to finish.
            </h2>
          </div>
          <Instructions items={SESSION_FLOW_STEPS} />
        </section>

        {/* § 08 — WHO IT'S FOR + ELIGIBILITY */}
        <section id="who" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 08 — Who it's for</div>
            <h2 className="iw-section-head__title">
              For members building steadier resilience.
            </h2>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-6">
            <Card divider>
              <p
                className="text-base sm:text-lg leading-relaxed mb-4"
                style={{ color: "var(--iw-body)" }}
              >
                For anyone who wants help winding down, managing everyday stress, recovering after
                a hard day, or building steadier resilience over time.
              </p>
              <p
                className="text-base leading-relaxed"
                style={{ color: "var(--iw-body)" }}
              >
                Before you start, a quick check-in helps make sure this is the right fit — and if
                it looks like you'd be better served by other support, our team will help you
                find it.
              </p>
            </Card>
            <div
              className="p-6"
              style={{
                background: "var(--iw-blue-tint)",
                border: "1px solid var(--iw-blue)",
                borderRadius: "2px",
              }}
              data-testid="eligibility-note"
            >
              <div
                className="iw-mono uppercase mb-2"
                style={{
                  color: "var(--iw-blue)",
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
              >
                Eligibility · Quick Check-in
              </div>
              <p
                className="text-[15px] leading-relaxed"
                style={{ color: "var(--iw-primary)" }}
              >
                The very first step is a six-question safety check-in. It's warm, non-clinical, and
                takes under a minute. Some members are routed to other forms of support — that's a
                feature, not a barrier.
              </p>
            </div>
          </div>
        </section>

        {/* § 09 — FAQ */}
        <section id="faq" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 09 — FAQ</div>
            <h2 className="iw-section-head__title">
              The real answers.
            </h2>
          </div>
          <div
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              background: "var(--iw-surface)",
            }}
            data-testid="faq-list"
          >
            {FAQ.map((item, i) => (
              <FAQItem key={item.q} n={String(i + 1).padStart(2, "0")} {...item} last={i === FAQ.length - 1} />
            ))}
          </div>
        </section>

        {/* Repeat CTA */}
        <section className="mt-20 fade-up">
          <div
            className="p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
            style={{
              background: "linear-gradient(135deg, #11296b 0%, #1e3a8a 100%)",
              borderRadius: "2px",
              boxShadow: "0 18px 50px -20px rgba(17, 41, 107, 0.35)",
            }}
          >
            <div>
              <div
                className="iw-mono uppercase mb-2"
                style={{
                  color: "#fca5a5",
                  fontSize: "11px",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
              >
                Ready when you are
              </div>
              <div
                className="serif text-2xl sm:text-3xl"
                style={{ color: "#fff" }}
              >
                Ready to reset?
              </div>
            </div>
            <button
              type="button"
              className="iw-btn-primary"
              onClick={submit(onStart)}
              data-testid="welcome-cta-repeat-btn"
            >
              {getAdaptiveCtaLabel(activation, "Start with a quick check-in")}{" "}
              <span aria-hidden="true">↗</span>
            </button>
          </div>
        </section>

        {/* Platform credit */}
        <section className="mt-16 fade-up">
          <Card className="!p-0 overflow-hidden" divider={false}>
            <div className="grid md:grid-cols-[1.4fr_1fr] gap-0 items-stretch">
              <div className="p-7 sm:p-8 flex items-start gap-5">
                <img
                  src={IW_SHIELD}
                  alt="Integrated Wellness"
                  width={56}
                  height={56}
                  className="flex-shrink-0"
                  style={{ filter: "drop-shadow(0 4px 12px rgba(17, 41, 107, 0.25))" }}
                />
                <div>
                  <div
                    className="iw-mono uppercase mb-2"
                    style={{
                      color: "var(--iw-accent-red)",
                      fontSize: "10px",
                      letterSpacing: "0.22em",
                      fontWeight: 600,
                    }}
                  >
                    Part of the Integrated Wellness Platform
                  </div>
                  <div className="serif text-xl sm:text-2xl" style={{ color: "var(--iw-primary)" }}>
                    Built for the Integrated Matrix Engine.
                  </div>
                  <div
                    className="text-sm sm:text-base mt-2 leading-relaxed"
                    style={{ color: "var(--iw-body)" }}
                  >
                    Native PHQ-4, GAD-7, SUDS check-ins · session timestamps for wearable
                    correlation (Apple Health · Oura · Whoop · Fitbit · Garmin) · Notion writeback
                    so your wellness team can review.
                  </div>
                  <div className="mt-4">
                    <PatentPendingBadge />
                  </div>
                </div>
              </div>
              <div
                className="relative overflow-hidden"
                style={{
                  minHeight: 220,
                  background: "linear-gradient(135deg, #11296b 0%, #1e3a8a 100%)",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/vxi2vvgb_7wpwsk8PLhVewnWOVnTWh.png"
                  alt="Smartwatch streaming heart-rate and vitals — wearable correlation"
                  className="absolute inset-0 w-full h-full object-cover opacity-90"
                  loading="lazy"
                  data-testid="platform-wearable-image"
                />
                <div
                  className="absolute bottom-3 left-4 iw-mono uppercase"
                  style={{
                    color: "rgba(255,255,255,0.92)",
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    fontWeight: 600,
                  }}
                >
                  HR · HRV · Sleep · Recovery
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* § 10 — Peak State (compact sibling-program teaser) */}
        <PeakStateTeaser />

        {/* § Additional Resource — AI Concierge */}
        <ConciergeChat />

        {/* § 11 — Ecosystem */}
        <EcosystemSection />

        {/* Wellness disclaimer block — visible, crawlable as text */}
        <section id="disclaimer" className="mt-12 fade-up" data-testid="wellness-disclaimer-block">
          <div
            className="p-6 sm:p-7"
            style={{
              background: "var(--iw-blue-tint)",
              border: "1px solid var(--iw-blue)",
              borderRadius: "2px",
            }}
          >
            <div
              className="iw-mono uppercase mb-2.5"
              style={{
                color: "var(--iw-blue)",
                fontSize: "10px",
                letterSpacing: "0.22em",
                fontWeight: 600,
              }}
            >
              Wellness Disclaimer
            </div>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--iw-primary)" }}>
              <strong>Nervous System Reset is a non-medical wellness program</strong> intended to
              support relaxation, recovery, and resilience. It is not medical or mental-health
              care, does not diagnose or treat any condition, and is not a substitute for advice
              from a licensed professional. If you have a medical or mental-health concern, please
              consult a qualified provider. If you are in crisis or thinking about harming
              yourself, call or text{" "}
              <strong>988 (Suicide &amp; Crisis Lifeline)</strong> or call{" "}
              <strong>911</strong>.
            </p>
          </div>
        </section>
      </div>
      <SiteFooter />
    </Aurora>
  );
}

function EcosystemSection() {
  return (
    <section
      id="ecosystem"
      className="mt-24 fade-up"
      data-testid="ecosystem-section"
    >
      <div className="iw-section-head">
        <div className="iw-section-head__label">§ 11 — The Ecosystem</div>
        <h2 className="iw-section-head__title">
          One platform. Two clear lanes — wellness and medical.
        </h2>
        <p className="text-base sm:text-lg max-w-3xl" style={{ color: "var(--iw-body)" }}>
          Integrated Wellness keeps coaching and clinical care side by side — but cleanly
          separated. Nervous System Reset and the 8 Programs are{" "}
          <strong style={{ color: "var(--iw-primary)" }}>wellness</strong>. Diagnosis, prescriptions
          and medically-supervised care live with the{" "}
          <strong style={{ color: "var(--iw-primary)" }}>licensed medical team</strong>.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        <EcosystemCard
          lane="Wellness · Non-Medical"
          laneColor="var(--iw-accent-red)"
          code="NSR · This Program"
          title="Nervous System Reset"
          copy="Short, guided wellness sessions — breathwork, grounding, bilateral rhythm, visualization, muscle release, journal. Self-guided, never diagnostic."
          ctaLabel="You're here"
          href={null}
          here
          testId="ecosystem-card-nsr"
        />
        <EcosystemCard
          lane="Wellness · Non-Medical"
          laneColor="var(--iw-accent-red)"
          code="PS · Sibling Program"
          title="Peak State"
          copy="Train the calm, focused 'in-the-zone' state through guided breathing and vivid mental rehearsal — with an optional in-house brain-activity session."
          ctaLabel="Jump to Peak State ↓"
          href="#peak-state"
          testId="ecosystem-card-peak-state"
        />
        <EcosystemCard
          lane="Wellness · Non-Medical"
          laneColor="var(--iw-accent-red)"
          code="8 Programs"
          title="Optimization Programs"
          copy="Eight personalized programs powered by your wearable data — body, metabolic, gut, longevity, performance, cognitive, hormonal vitality, life. Coaching, not diagnosis."
          ctaLabel="Explore Programs"
          href={PROGRAMS_URL}
          testId="ecosystem-card-programs"
        />
        <EcosystemCard
          lane="Medical · Licensed Care"
          laneColor="var(--iw-blue)"
          code="IM · Clinical"
          title="Integrated Medical"
          copy="Diagnoses, prescriptions, hormones, deeper diagnostics — anything that requires medically-supervised care is routed to our licensed Integrated Medical team."
          ctaLabel="Medical Care"
          href={MEDICAL_URL}
          medical
          testId="ecosystem-card-medical"
        />
      </div>
    </section>
  );
}

function EcosystemCard({ lane, laneColor, code, title, copy, ctaLabel, href, here, medical, testId }) {
  const Wrap = href ? "a" : "div";
  let wrapProps = {};
  if (href) {
    const isExternal = href.startsWith("http");
    wrapProps = isExternal ? { href, target: "_blank", rel: "noopener noreferrer" } : { href };
  }
  return (
    <Wrap
      {...wrapProps}
      data-testid={testId}
      className="iw-card p-6 sm:p-7 relative flex flex-col transition-all"
      style={{
        borderTop: `3px solid ${laneColor}`,
        cursor: href ? "pointer" : "default",
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <span
          className="iw-mono uppercase"
          style={{
            color: laneColor,
            fontSize: "10px",
            letterSpacing: "0.20em",
            fontWeight: 600,
          }}
        >
          {lane}
        </span>
        {here && (
          <span
            className="iw-mono uppercase px-2 py-0.5"
            style={{
              background: "var(--iw-primary)",
              color: "#fff",
              borderRadius: "2px",
              fontSize: "9px",
              letterSpacing: "0.18em",
              fontWeight: 600,
            }}
          >
            You're here
          </span>
        )}
        {medical && (
          <span
            className="iw-mono uppercase px-2 py-0.5"
            style={{
              border: `1px solid ${laneColor}`,
              color: laneColor,
              borderRadius: "2px",
              fontSize: "9px",
              letterSpacing: "0.18em",
              fontWeight: 600,
            }}
          >
            Licensed
          </span>
        )}
      </div>
      <span
        className="iw-mono uppercase mb-1.5"
        style={{ color: "var(--iw-muted)", fontSize: "11px", letterSpacing: "0.16em", fontWeight: 600 }}
      >
        {code}
      </span>
      <h3 className="serif text-xl sm:text-2xl mb-2" style={{ color: "var(--iw-primary)" }}>
        {title}
      </h3>
      <p className="text-[14.5px] leading-relaxed flex-1" style={{ color: "var(--iw-body)" }}>
        {copy}
      </p>
      <div
        className="mt-5 pt-4 iw-mono uppercase flex items-center justify-between"
        style={{
          borderTop: "1px solid var(--iw-border)",
          color: href ? "var(--iw-primary)" : "var(--iw-muted)",
          fontSize: "11px",
          letterSpacing: "0.18em",
          fontWeight: 600,
        }}
      >
        <span>{ctaLabel}</span>
        {href && <span aria-hidden="true">↗</span>}
      </div>
    </Wrap>
  );
}

function ToolkitCard({
  code,
  flag,
  flagTone,
  title,
  sub,
  copy,
  image,
  imageAlt,
  prompt,
  onLaunch,
  highlighted = false,
  highlightTone = null,
  highlightRank = -1,
}) {
  const flagStyle =
    flagTone === "featured"
      ? {
          background: "var(--iw-accent-red)",
          color: "#fff",
        }
      : {
          background: "var(--iw-surface)",
          color: "var(--iw-primary)",
          border: "1px solid var(--iw-border)",
        };
  const clickable = !!(prompt && onLaunch);
  const Tag = clickable ? "button" : "div";
  const handleClick = clickable
    ? () => onLaunch(prompt, title)
    : undefined;
  // L2 Adaptive Routing — dim non-recommended cards + add a numbered step
  // badge to recommended ones so the sequence reads at a glance.
  const highlightBorder = highlighted
    ? getBandTint(highlightTone).border
    : "var(--iw-accent-red)";
  const dim = !highlighted && highlightTone !== null;
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={handleClick}
      className={`iw-toolkit-card iw-card relative flex flex-col overflow-hidden text-left w-full ${
        clickable ? "iw-toolkit-card--clickable" : ""
      }`}
      data-testid={`toolkit-${code.toLowerCase()}`}
      data-highlighted={highlighted ? "true" : "false"}
      style={{
        borderTop: `3px solid ${highlightBorder}`,
        boxShadow: highlighted
          ? `0 12px 32px -18px ${highlightBorder}`
          : undefined,
        opacity: dim ? 0.55 : 1,
        transition: "opacity 220ms ease, box-shadow 220ms ease",
      }}
      aria-label={clickable ? `Ask Elara — ${title}` : undefined}
    >
      {highlighted && highlightRank >= 0 && (
        <span
          className="absolute z-10 iw-mono uppercase"
          data-testid={`toolkit-rank-${code.toLowerCase()}`}
          style={{
            top: -12,
            left: -8,
            background: highlightBorder,
            color: "#fff",
            fontSize: "12px",
            letterSpacing: "0.05em",
            fontWeight: 800,
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "999px",
            boxShadow: "0 6px 16px rgba(15,26,46,0.28)",
            border: "2px solid #fff",
          }}
        >
          {highlightRank + 1}
        </span>
      )}
      {image && (
        <div
          className="iw-toolkit-card__media relative overflow-hidden"
          style={{
            aspectRatio: "16 / 10",
            background: "linear-gradient(160deg, #11296b 0%, #1e3a8a 100%)",
          }}
        >
          <img
            src={image}
            alt={imageAlt || title}
            className="iw-toolkit-card__img block w-full h-full object-cover"
            loading="lazy"
            data-testid={`toolkit-img-${code.toLowerCase()}`}
          />
          <span
            className="absolute top-3 left-3 iw-mono uppercase px-2 py-1"
            style={{
              ...flagStyle,
              borderRadius: "2px",
              fontSize: "9px",
              letterSpacing: "0.18em",
              fontWeight: 600,
              zIndex: 2,
            }}
          >
            {flag}
          </span>
          <span
            className="absolute top-3 right-3 iw-badge"
            style={{ fontSize: "9px", letterSpacing: "0.18em", zIndex: 2 }}
          >
            {code}
          </span>
          {clickable && (
            <span
              className="iw-toolkit-card__cta"
              aria-hidden="true"
            >
              <span className="iw-mono uppercase">Ask Elara</span>
              <span aria-hidden="true">↗</span>
            </span>
          )}
        </div>
      )}
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <h3 className="serif text-xl sm:text-2xl mb-2" style={{ color: "var(--iw-primary)" }}>
          {title}
        </h3>
        <p
          className="text-base mb-3"
          style={{ color: "var(--iw-accent-red)", fontWeight: 600 }}
        >
          {sub}
        </p>
        <p className="text-[14.5px] leading-relaxed flex-1" style={{ color: "var(--iw-body)" }}>
          {copy}
        </p>
      </div>
    </Tag>
  );
}

function ModeCard({ code, flag, title, sub, copy, image, imageAlt }) {
  return (
    <div
      className="iw-toolkit-card iw-card relative flex flex-col overflow-hidden"
      data-testid={`mode-card-${code.toLowerCase()}`}
    >
      {image && (
        <div
          className="iw-toolkit-card__media relative overflow-hidden"
          style={{
            aspectRatio: "16 / 10",
            background: "linear-gradient(160deg, #11296b 0%, #1e3a8a 100%)",
          }}
        >
          <img
            src={image}
            alt={imageAlt || title}
            className="iw-toolkit-card__img block w-full h-full object-cover"
            loading="lazy"
            data-testid={`mode-img-${code.toLowerCase()}`}
          />
          <span
            className="absolute top-3 left-3 iw-badge"
            style={{ fontSize: "9px", letterSpacing: "0.18em", zIndex: 2 }}
          >
            {code}
          </span>
          <span
            className="absolute top-3 right-3 iw-badge iw-badge--ghost"
            style={{ fontSize: "9px", letterSpacing: "0.18em", zIndex: 2 }}
          >
            {flag}
          </span>
        </div>
      )}
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <h3 className="serif text-2xl sm:text-3xl mb-2" style={{ color: "var(--iw-primary)" }}>
          {title}
        </h3>
        <p className="text-base mb-3" style={{ color: "var(--iw-accent-red)", fontWeight: 600 }}>
          {sub}
        </p>
        <p className="text-base leading-relaxed" style={{ color: "var(--iw-body)" }}>
          {copy}
        </p>
      </div>
    </div>
  );
}

function FAQItem({ n, q, a, last }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left px-5 sm:px-7 py-5 transition-colors"
      style={{
        borderBottom: last ? "none" : "1px solid var(--iw-border)",
        background: open ? "var(--iw-blue-tint)" : "transparent",
      }}
      data-testid={`faq-item-${n}`}
      aria-expanded={open}
    >
      <div className="flex items-start gap-4">
        <span
          className="iw-mono flex-shrink-0"
          style={{
            color: "var(--iw-accent-red)",
            fontSize: "12px",
            letterSpacing: "0.16em",
            fontWeight: 600,
          }}
        >
          {n}
        </span>
        <div className="flex-1">
          <div
            className="serif text-lg sm:text-xl flex items-start justify-between gap-4"
            style={{ color: "var(--iw-primary)", fontWeight: 700 }}
          >
            <span>{q}</span>
            <span
              aria-hidden="true"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "20px",
                lineHeight: 1,
                transform: open ? "rotate(45deg)" : "rotate(0)",
                transition: "transform 200ms ease",
              }}
            >
              +
            </span>
          </div>
          {open && (
            <p
              className="mt-3 text-base leading-relaxed"
              style={{ color: "var(--iw-body)" }}
            >
              {a}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function Pill({ children }) {
  return (
    <span
      className="iw-mono uppercase px-2.5 py-1"
      style={{
        border: "1px solid var(--iw-border)",
        borderRadius: "2px",
        background: "var(--iw-surface)",
        color: "var(--iw-primary)",
        fontSize: "10px",
        letterSpacing: "0.18em",
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}


/**
 * StepCard — clickable "How it works" step that wakes Elara with a
 * step-specific question. Falls back to a non-interactive card if no
 * `onLaunch` handler is supplied.
 */
function StepCard({ step, onLaunch }) {
  const clickable = !!(step.elaraPrompt && onLaunch);
  const Tag = clickable ? "button" : "div";
  const handleClick = clickable
    ? () => onLaunch(step.elaraPrompt, `Step ${step.n} · ${step.title.replace(/\.$/, "")}`)
    : undefined;
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={handleClick}
      className={`iw-step-card iw-card p-5 sm:p-6 relative text-left w-full ${
        clickable ? "iw-step-card--clickable" : ""
      }`}
      style={{ borderTop: "3px solid var(--iw-accent-red)" }}
      data-testid={`how-step-${step.n}`}
      aria-label={clickable ? `Ask Elara — Step ${step.n}: ${step.title}` : undefined}
    >
      <div
        className="iw-mono mb-3"
        style={{
          color: "var(--iw-accent-red)",
          fontSize: "11px",
          letterSpacing: "0.18em",
          fontWeight: 600,
        }}
      >
        Step {step.n}
      </div>
      <div
        className="serif text-lg mb-2"
        style={{ color: "var(--iw-primary)", fontWeight: 700 }}
      >
        {step.title}
      </div>
      <p
        className="text-[14px] leading-relaxed"
        style={{ color: "var(--iw-body)" }}
      >
        {step.copy}
      </p>
      {clickable && (
        <span className="iw-step-card__cta" aria-hidden="true">
          <span className="iw-mono uppercase">Ask Elara</span>
          <span aria-hidden="true">↗</span>
        </span>
      )}
    </Tag>
  );
}
function AgentPromptToast({ toast, onClose }) {
  // Confirms one of the AI coaches (Maya or Elara) opened and walks the
  // member through the paste-and-send flow with 3 numbered steps.
  if (!toast) return null;
  const { agentName = "Elara", label, prompt, opened, copied, pasteShortcut } = toast;
  const headline = opened
    ? `${agentName} is open · ${label}`
    : `Opening ${agentName} in a new tab · ${label}`;
  const pasteHint = copied
    ? `Paste it (${pasteShortcut})`
    : `Type or paste it (${pasteShortcut})`;
  const steps = [
    { n: "1", text: `Click the chat input that says “Type here” in ${agentName}’s window.` },
    { n: "2", text: pasteHint },
    { n: "3", text: "Press the send button (or Enter)." },
  ];
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="maya-toast"
      className="iw-maya-toast"
    >
      <div
        className="iw-mono uppercase mb-2"
        style={{
          color: "var(--iw-accent-red)",
          fontSize: "10px",
          letterSpacing: "0.22em",
          fontWeight: 700,
        }}
      >
        {headline}
      </div>
      <div
        className="text-[13px] leading-relaxed mb-3"
        style={{ color: "var(--iw-primary)", fontWeight: 600 }}
      >
        {copied ? "We copied this prompt for you:" : `Use this prompt with ${agentName}:`}
      </div>
      <div
        className="iw-mono text-[12px] leading-relaxed mb-3"
        style={{
          color: "var(--iw-primary)",
          background: "var(--iw-blue-tint)",
          border: "1px solid var(--iw-border)",
          padding: "0.5rem 0.7rem",
          borderRadius: "2px",
          letterSpacing: "0.02em",
        }}
      >
        “{prompt}”
      </div>
      <ol className="space-y-1.5 list-none m-0 p-0" data-testid="maya-toast-steps">
        {steps.map((s) => (
          <li key={s.n} className="flex items-start gap-2.5 leading-snug">
            <span
              className="iw-mono flex-shrink-0 mt-0.5"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "11px",
                fontWeight: 700,
                width: 14,
              }}
            >
              {s.n}
            </span>
            <span
              className="text-[12.5px]"
              style={{ color: "var(--iw-body)" }}
            >
              {s.text}
            </span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onClose}
        className="iw-mono uppercase mt-3 inline-flex items-center gap-1.5"
        style={{
          color: "var(--iw-muted)",
          fontSize: "10px",
          letterSpacing: "0.18em",
          fontWeight: 600,
          background: "none",
          border: "none",
          cursor: "pointer",
        }}
        data-testid="maya-toast-close"
      >
        Got it ✕
      </button>
    </div>
  );
}

/**
 * AskMayaHowTo — persistent callout above the Toolkit grid that teaches
 * the click → paste → send flow once, so members know what to expect.
 */
function AskMayaHowTo({ className = "" }) {
  return (
    <div
      className={`flex items-start gap-4 p-4 sm:p-5 ${className}`}
      style={{
        background: "var(--iw-blue-tint)",
        border: "1px solid var(--iw-border)",
        borderRadius: "2px",
      }}
      data-testid="ask-maya-howto"
    >
      <span
        className="iw-badge flex-shrink-0 mt-0.5"
        style={{ background: "var(--iw-accent-red)", color: "#fff" }}
      >
        Tip
      </span>
      <div className="flex-1">
        <div
          className="iw-mono uppercase mb-1"
          style={{
            color: "var(--iw-primary)",
            fontSize: "10px",
            letterSpacing: "0.22em",
            fontWeight: 700,
          }}
        >
          Click a card to ask Elara
        </div>
        <p
          className="text-[13.5px] leading-relaxed m-0"
          style={{ color: "var(--iw-body)" }}
        >
          Click any practice below. Elara's chat will open and we'll copy a
          ready-made prompt to your clipboard. In Elara's input field, paste it
          and press send — she'll walk you through the technique step by step.
        </p>
      </div>
    </div>
  );
}

/**
 * CheckinCard — member check-in touchpoint. Opens the JotForm in a new
 * tab; submissions sync to the IW Notion record on the JotForm side.
 */
function CheckinCard({ step, title, copy, cta, href, accent, testId, onCtaClick }) {
  return (
    <div
      className="iw-card p-6 sm:p-7 flex flex-col"
      style={{ borderTop: `3px solid ${accent}` }}
      data-testid={testId}
    >
      <div
        className="iw-mono uppercase mb-2"
        style={{ color: accent, fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}
      >
        {step}
      </div>
      <div className="serif text-xl sm:text-2xl mb-2" style={{ color: "var(--iw-primary)" }}>
        {title}
      </div>
      <p className="text-[15px] leading-relaxed flex-1 m-0" style={{ color: "var(--iw-body)" }}>
        {copy}
      </p>
      <div className="mt-5">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="iw-btn-primary"
          onClick={onCtaClick}
          data-testid={`${testId}-btn`}
        >
          {cta} <span aria-hidden="true">↗</span>
        </a>
      </div>
    </div>
  );
}

/**
 * ActivationDial — hero-embedded 0–10 slider that captures the member's
 * self-reported activation before they've entered the session flow.
 * The moment it's touched, the adaptive plan below unlocks and the hero
 * CTA relabels itself to match the first step. Skippable — leaving it
 * untouched preserves the original neutral welcome experience.
 */
function ActivationDial({ activation, setActivation, plan }) {
  const value = activation === null ? "" : activation;
  const displayValue = activation === null ? "—" : activation;
  const tint = plan ? getBandTint(plan.tone) : null;

  return (
    <div
      className="w-full"
      data-testid="welcome-activation-dial"
      style={{
        border: "1px solid var(--iw-border)",
        borderTop: plan
          ? `3px solid ${tint.border}`
          : "3px solid var(--iw-border)",
        borderRadius: "2px",
        background: plan ? tint.surface : "var(--iw-surface, #FFF)",
        padding: "1rem 1.15rem",
        transition: "background 220ms ease, border-color 220ms ease",
      }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
        <label
          htmlFor="welcome-activation"
          className="iw-mono uppercase"
          style={{
            color: plan ? tint.label : "var(--iw-muted)",
            fontSize: "10px",
            letterSpacing: "0.22em",
            fontWeight: 700,
          }}
        >
          How activated do you feel right now? <span style={{ opacity: 0.7 }}>(optional)</span>
        </label>
        <span
          className="iw-mono"
          data-testid="welcome-activation-value"
          style={{
            color: plan ? tint.label : "var(--iw-primary)",
            fontSize: "13px",
            fontWeight: 700,
            letterSpacing: "0.05em",
          }}
        >
          {displayValue}
          <span style={{ opacity: 0.55, fontWeight: 500 }}> / 10</span>
        </span>
      </div>
      <input
        id="welcome-activation"
        type="range"
        min={0}
        max={10}
        step={1}
        value={value === "" ? 0 : value}
        onChange={(e) => setActivation(Number(e.target.value))}
        onInput={(e) => setActivation(Number(e.target.value))}
        onPointerDown={() => {
          // Commit the current slider position even if the user "drags" to
          // the same value (i.e. 0 from the pristine state). Without this
          // the untouched thumb at 0 never fires an onInput/onChange.
          if (activation === null) setActivation(0);
        }}
        onKeyDown={() => {
          if (activation === null) setActivation(0);
        }}
        className="w-full"
        aria-valuemin={0}
        aria-valuemax={10}
        aria-valuenow={value === "" ? 0 : value}
        aria-label="Activation 0 to 10"
        data-testid="welcome-activation-slider"
        style={{
          accentColor: plan ? tint.border : "var(--iw-blue, #1e3a8a)",
          cursor: "pointer",
        }}
      />
      <div
        className="flex justify-between iw-mono"
        style={{
          color: "var(--iw-muted)",
          fontSize: "9.5px",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginTop: 4,
        }}
      >
        <span>0 · Calm</span>
        <span>5 · Moderate</span>
        <span>10 · Maxed</span>
      </div>
      {plan && (
        <div
          className="mt-3 pt-3"
          data-testid="welcome-adaptive-plan"
          style={{ borderTop: "1px dashed var(--iw-border)" }}
        >
          <div
            className="iw-mono uppercase mb-1"
            style={{
              color: tint.label,
              fontSize: "10px",
              letterSpacing: "0.22em",
              fontWeight: 700,
            }}
          >
            Suggested path · {plan.label}
          </div>
          <div
            className="text-[13px] leading-relaxed mb-2"
            style={{ color: "var(--iw-body)" }}
            data-testid="welcome-adaptive-plan-detail"
          >
            {plan.detail}
          </div>
          <ol
            className="flex flex-col gap-1 m-0 p-0 list-none"
            data-testid="welcome-adaptive-plan-chain"
          >
            {plan.chain.map((step, idx) => (
              <li
                key={step.code}
                className="flex items-start gap-2.5 text-[12.5px] leading-snug"
                style={{ color: "var(--iw-primary)" }}
                data-testid={`welcome-adaptive-step-${idx + 1}`}
              >
                <span
                  className="iw-mono flex-shrink-0"
                  style={{
                    background: tint.border,
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 700,
                    width: 18,
                    height: 18,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "999px",
                    marginTop: 1,
                  }}
                >
                  {idx + 1}
                </span>
                <span>
                  <strong style={{ fontWeight: 700 }}>{step.title}</strong>
                  <span style={{ color: "var(--iw-muted)" }}> · {step.why}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/**
 * ToolkitAdaptiveBanner — mirrors the hero plan inside the Toolkit section
 * so a member who scrolls past the hero still sees why some cards are
 * emphasized. Small; the numbered cards do the heavy visual work.
 */
function ToolkitAdaptiveBanner({ plan, activation }) {
  const tint = getBandTint(plan.tone);
  return (
    <div
      className="mb-5 p-4 sm:p-5"
      data-testid="toolkit-adaptive-banner"
      style={{
        background: tint.surface,
        borderLeft: `3px solid ${tint.border}`,
        borderRadius: "2px",
      }}
    >
      <div
        className="iw-mono uppercase mb-1"
        style={{
          color: tint.label,
          fontSize: "10px",
          letterSpacing: "0.22em",
          fontWeight: 700,
        }}
      >
        Your path today · activation {activation} / 10
      </div>
      <div
        className="text-[14px] leading-relaxed"
        style={{ color: "var(--iw-primary)", fontWeight: 500 }}
      >
        {plan.chain.map((step, idx) => (
          <React.Fragment key={step.code}>
            <span
              className="iw-mono"
              style={{
                background: tint.border,
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
                padding: "1px 6px",
                marginRight: 4,
                borderRadius: "999px",
                letterSpacing: "0.02em",
              }}
            >
              {idx + 1}
            </span>
            <strong style={{ fontWeight: 700 }}>{step.title}</strong>
            {idx < plan.chain.length - 1 && (
              <span
                aria-hidden="true"
                style={{ margin: "0 8px", color: "var(--iw-muted)" }}
              >
                →
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

